import { useEffect, useMemo, useState } from 'react';
import AccessDenied from './AccessDenied';
import { loadLeads, rejectLead, approveLead, type ApproveResult } from '../../lib/onboardingLeadsService';
import {
  pendingLeads,
  countByStatus,
  suggestSlug,
  isValidSlug,
  type OnboardingLead,
} from '../../lib/onboardingLeads';

/**
 * Onglet « Demandes » du SuperAdmin : valide en 1 clic les demandes d'inscription
 * arrivées du formulaire yumlink.io. Valider → crée le site + envoie l'email au
 * resto (Edge Function approve-lead → provision). Rejeter → test/spam archivé.
 */
export default function OnboardingLeadsSection() {
  const [leads, setLeads] = useState<OnboardingLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [slugs, setSlugs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [done, setDone] = useState<Record<string, ApproveResult>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const refresh = () => {
    setLoading(true);
    loadLeads().then((res) => {
      if (res.ok) {
        setLeads(res.leads);
        // Pré-remplit le slug proposé pour chaque demande en attente.
        setSlugs((prev) => {
          const next = { ...prev };
          for (const l of res.leads) {
            if (l.status === 'a_valider' && !next[l.id]) next[l.id] = l.slug || suggestSlug(l.name);
          }
          return next;
        });
        setError(null);
      } else {
        setError(res.error ?? 'Chargement impossible.');
      }
      setLoading(false);
    });
  };

  useEffect(refresh, []);

  const pending = useMemo(() => pendingLeads(leads), [leads]);
  const counts = useMemo(() => countByStatus(leads), [leads]);

  const onReject = async (id: string) => {
    setBusy((b) => ({ ...b, [id]: true }));
    const res = await rejectLead(id);
    setBusy((b) => ({ ...b, [id]: false }));
    if (!res.ok) {
      setRowError((e) => ({ ...e, [id]: res.error ?? 'Rejet impossible.' }));
      return;
    }
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status: 'rejete' } : l)));
  };

  const onApprove = async (lead: OnboardingLead) => {
    const slug = (slugs[lead.id] || '').trim().toLowerCase();
    if (!isValidSlug(slug)) {
      setRowError((e) => ({ ...e, [lead.id]: 'Adresse invalide : minuscules, chiffres et tirets (3 à 40 car.).' }));
      return;
    }
    setRowError((e) => ({ ...e, [lead.id]: '' }));
    setBusy((b) => ({ ...b, [lead.id]: true }));
    const res = await approveLead(lead.id, slug);
    setBusy((b) => ({ ...b, [lead.id]: false }));
    if (!res.ok) {
      setRowError((e) => ({ ...e, [lead.id]: res.error ?? 'Validation impossible.' }));
      return;
    }
    setDone((d) => ({ ...d, [lead.id]: res }));
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status: 'cree', slug } : l)));
  };

  const toggleSelect = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const pendingIds = pending.map((l) => l.id);
  const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(pendingIds));

  /** Valide en une fois toutes les demandes cochées (séquentiel, slug pré-rempli). */
  const bulkApprove = async () => {
    const targets = pending.filter((l) => selected.has(l.id));
    if (targets.length === 0) return;
    setBulkBusy(true);
    for (const lead of targets) {
      const slug = (slugs[lead.id] || suggestSlug(lead.name)).trim().toLowerCase();
      if (!isValidSlug(slug)) {
        setRowError((e) => ({ ...e, [lead.id]: 'Adresse invalide — demande ignorée.' }));
        continue;
      }
      setBusy((b) => ({ ...b, [lead.id]: true }));
      const res = await approveLead(lead.id, slug);
      setBusy((b) => ({ ...b, [lead.id]: false }));
      if (res.ok) {
        setDone((d) => ({ ...d, [lead.id]: res }));
        setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status: 'cree', slug } : l)));
      } else {
        setRowError((e) => ({ ...e, [lead.id]: res.error ?? 'Validation impossible.' }));
      }
    }
    setSelected(new Set());
    setBulkBusy(false);
  };

  if (error) {
    return <AccessDenied title="Demandes" sqlFile="sql/30_onboarding_leads.sql" />;
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-4 rounded-xl p-4 text-white" style={{ background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)' }}>
        <div className="text-xs font-bold uppercase opacity-80">📥 Demandes d'inscription</div>
        <div className="text-lg font-extrabold">Valide ou rejette les nouvelles demandes</div>
        <div className="text-sm opacity-90">Arrivées du formulaire yumlink.io. Valider crée le site et envoie l'email au resto.</div>
      </div>

      <div className="mb-5 grid grid-cols-3 gap-3">
        <Kpi label="⏳ À valider" value={String(counts.a_valider)} tone={counts.a_valider ? 'warn' : 'good'} />
        <Kpi label="✅ Sites créés" value={String(counts.cree)} tone="good" />
        <Kpi label="🚫 Rejetées" value={String(counts.rejete)} tone="neutral" />
      </div>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-bold">Demandes en attente ({pending.length})</h2>
        <div className="flex items-center gap-2">
          {pending.length > 0 && (
            <>
              <label className="flex items-center gap-1 text-sm text-slate-600">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} /> Tout
              </label>
              <button
                onClick={bulkApprove}
                disabled={bulkBusy || selected.size === 0}
                className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: '#16A34A' }}
              >
                {bulkBusy ? 'Validation…' : `✅ Valider la sélection${selected.size ? ` (${selected.size})` : ''}`}
              </button>
            </>
          )}
          <button onClick={refresh} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50">
            ↻ Rafraîchir
          </button>
        </div>
      </div>

      {loading ? (
        <p className="opacity-70">Chargement des demandes…</p>
      ) : pending.length === 0 ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
          ✓ Aucune demande en attente. Les nouvelles demandes du formulaire apparaîtront ici.
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {pending.map((lead) => {
            const result = done[lead.id];
            return (
              <li key={lead.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selected.has(lead.id)}
                      onChange={() => toggleSelect(lead.id)}
                      className="mt-1"
                      aria-label={`Sélectionner ${lead.name}`}
                    />
                    <div>
                    <div className="font-bold">
                      {lead.name} {lead.ville && <span className="opacity-50">· {lead.ville}</span>}
                    </div>
                    <div className="mt-0.5 text-sm text-slate-500">
                      {lead.cuisine && <span>{lead.cuisine} · </span>}
                      {lead.email}
                      {lead.tel && <span> · {lead.tel}</span>}
                    </div>
                    {lead.lien_menu && (
                      <div className="mt-0.5 text-xs">
                        Menu : <a href={lead.lien_menu} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{lead.lien_menu}</a>
                      </div>
                    )}
                    <div className="mt-0.5 text-xs text-slate-400">{new Date(lead.created_at).toLocaleString('fr-FR')}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <label className="text-sm text-slate-500">Adresse du site :</label>
                  <span className="text-sm text-slate-400">/r/</span>
                  <input
                    value={slugs[lead.id] ?? ''}
                    onChange={(e) => setSlugs((s) => ({ ...s, [lead.id]: e.target.value }))}
                    disabled={busy[lead.id]}
                    className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
                    placeholder="mon-resto"
                  />
                  <button
                    onClick={() => onApprove(lead)}
                    disabled={busy[lead.id]}
                    className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                    style={{ background: '#16A34A' }}
                  >
                    {busy[lead.id] ? '…' : '✅ Valider'}
                  </button>
                  <button
                    onClick={() => onReject(lead.id)}
                    disabled={busy[lead.id]}
                    className="rounded-lg border border-red-300 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    🚫 Rejeter
                  </button>
                </div>

                {rowError[lead.id] && <p className="mt-2 text-sm text-red-600">{rowError[lead.id]}</p>}

                {result && (
                  <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
                    ✅ Site créé : <a href={result.sitePath} className="underline">{result.sitePath}</a>
                    {' · '}
                    <a href={result.adminPath} className="underline">admin</a>
                    <div className="mt-1 text-xs">
                      Identifiant : <b>{result.email}</b> · Mot de passe provisoire : <b>{result.tempPassword}</b>
                      {' · '}
                      {result.emailSent ? '📧 email envoyé' : '⚠️ email non envoyé (à renvoyer)'}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone: 'good' | 'warn' | 'bad' | 'neutral' }) {
  const color = tone === 'bad' ? '#DC2626' : tone === 'warn' ? '#D97706' : tone === 'good' ? '#16A34A' : '#0F172A';
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-xs uppercase opacity-50">{label}</div>
      <div className="text-2xl font-extrabold" style={{ color }}>{value}</div>
    </div>
  );
}
